import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AccountCircle, CheckCircle, Logout, WarningAmber } from '@mui/icons-material';
import { useAuthStore } from '@/store';
import { useHome } from '@/hooks/useApi';
import { useMePlayer } from '@/hooks/useManage';
import { loginPath } from '@/utils/loginRedirect';
import { parseJwt } from '@/utils/jwt';

type LinkRow = { id: string; title: string; subtitle?: string; href: string };

const LinkSection: React.FC<{ id: string; title: string; rows: LinkRow[]; empty: string }> = ({ id, title, rows, empty }) => {
  const navigate = useNavigate();
  return (
    <Box id={id} sx={{ mb: 2, scrollMarginTop: 72 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Paper>
        {rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {empty}
          </Typography>
        ) : (
          <List disablePadding>
            {rows.map((row) => (
              <ListItemButton key={row.id} onClick={() => navigate(row.href)} sx={{ minHeight: 56 }}>
                <ListItemText primary={row.title} secondary={row.subtitle} />
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { isAuthenticated, logout } = useAuthStore();
  const { data: me } = useMePlayer();
  const player = me?.player;
  const { data: home } = useHome(undefined, { enabled: isAuthenticated });
  const token = localStorage.getItem('auth_token') ?? '';
  const payload = parseJwt(token);

  const email = payload?.sub ?? 'Не удалось определить';
  const isEmailVerified = Boolean(payload?.ev);

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [hash, home]);

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Войдите, чтобы увидеть свой профиль.
        </Alert>
        <Button variant="contained" onClick={() => navigate(loginPath('/profile'))} sx={{ minHeight: 48 }}>
          Войти
        </Button>
      </Box>
    );
  }

  const mine = home?.myParticipation;
  const tournaments: LinkRow[] = [...(mine?.tournamentsAsOrganizer ?? []), ...(mine?.tournamentsAsPlayer ?? [])]
    .filter((t, i, all) => all.findIndex((x) => x.id === t.id) === i)
    .map((t) => ({ id: t.id, title: t.name, subtitle: t.season ?? undefined, href: `/tournaments/${t.id}` }));
  const teams: LinkRow[] = (mine?.teamsAsManager ?? []).map((t) => ({ id: t.id, title: t.name, href: `/teams/${t.id}` }));
  const favorites: LinkRow[] = [
    ...(home?.favorites?.tournaments ?? []).map((t) => ({ id: t.id, title: t.name, subtitle: 'Турнир', href: `/tournaments/${t.id}` })),
    ...(home?.favorites?.matches ?? []).map((m) => ({ id: m.id, title: 'Матч', href: `/matches/${m.id}` })),
  ];
  const playerName = player?.id ? [player.firstName, player.lastName].filter(Boolean).join(' ') : '';

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <AccountCircle />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" noWrap>
                {playerName || email}
              </Typography>
              {playerName && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {email}
                </Typography>
              )}
              <Chip
                size="small"
                sx={{ mt: 0.5 }}
                color={isEmailVerified ? 'success' : 'warning'}
                icon={isEmailVerified ? <CheckCircle /> : <WarningAmber />}
                label={isEmailVerified ? 'Email подтверждён' : 'Email не подтверждён'}
              />
            </Box>
          </Stack>
          {player?.id ? (
            <Button fullWidth variant="outlined" onClick={() => navigate(`/players/${player.id}`)} sx={{ mt: 2, minHeight: 48 }}>
              Моя карточка игрока
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Карточка игрока появится, когда вы впервые отметитесь на тренировке или вас добавят в состав.
            </Typography>
          )}
        </CardContent>
      </Card>

      <LinkSection id="tournaments" title="Мои турниры" rows={tournaments} empty="Вы пока не участвуете в турнирах." />
      <LinkSection id="teams" title="Мои команды" rows={teams} empty="Вы не управляете командами." />
      <LinkSection id="favorites" title="Избранное" rows={favorites} empty="В избранном пусто." />

      <Button
        fullWidth
        variant="outlined"
        color="error"
        startIcon={<Logout />}
        onClick={() => {
          logout();
          navigate('/');
        }}
        sx={{ mt: 1, minHeight: 48 }}
      >
        Выйти
      </Button>
    </Box>
  );
};
